import type { CSSProperties } from "react";

import BacklogTable from "@/components/BacklogTable";
import { useSelectedSubsheet } from "@/hooks/useSelectedSubsheet";
import { useSubsheets } from "@/hooks/useSubsheets";
import { ALL_ITEMS_ID } from "@/lib/constants";

const Index = () => {
  const { selectedSubsheetId } = useSelectedSubsheet();
  const isAllItems = selectedSubsheetId === ALL_ITEMS_ID;
  const { data: subsheets = [], isLoading: isLoadingSubsheets } = useSubsheets();
  const selectedSubsheet = subsheets.find((s) => s.id === selectedSubsheetId);

  const headerTitle = isAllItems ? "All Backlog Items" : (selectedSubsheet?.name ?? "Backlog");
  const headerSubtitle = !isAllItems ? (selectedSubsheet?.description ?? null) : null;

  return (
    /* Keyed by the department, which is what makes switching one feel like
       arriving somewhere.

       Every department is the same route with a different `subsheetId` prop,
       so React reconciles the existing header and table in place and the
       entrance never replays — you change scope and nothing moves, which reads
       as the click not having landed. The key forces a remount, so the whole
       ladder (header 20ms, toolbar 80ms, table 140ms) runs again exactly as it
       does on a real navigation. Collapsed sections and scroll position reset
       with it, which is the right answer anyway: they belonged to the
       department you just left. */
    <div key={selectedSubsheetId} className="flex min-h-0 flex-1 flex-col">
      {/* First rung of the page's entrance ladder; the toolbar and the table
          are the next two, inside BacklogTable (see `.page-rise`). */}
      <div
        style={{ "--rise-delay": "20ms" } as CSSProperties}
        className="page-rise flex items-start justify-between gap-3 border-b border-border px-4 lg:px-6 py-3 bg-background"
      >
        <div className="min-w-0 flex-1 ml-12 lg:ml-0">
          {isLoadingSubsheets && !selectedSubsheet ? (
            <div className="h-7 w-48 bg-muted animate-pulse rounded" />
          ) : (
            <h1 className="text-xl lg:text-2xl font-bold text-foreground truncate">{headerTitle}</h1>
          )}
          {/* Always rendered, even when empty: a department with a blurb and
              one without must occupy the same height, or the toolbar below
              shifts every time you switch scope. */}
          <p
            className="mt-0.5 line-clamp-1 hidden text-xs italic text-muted-foreground sm:block"
            title={headerSubtitle ?? undefined}
          >
            {headerSubtitle || " "}
          </p>
        </div>
        {/* "Copy Shareable Link" is gone. It copied the app's origin — the same
            URL for every department and every filter — so it shared nothing
            the address bar didn't already have. */}
      </div>
      <div className="flex-1 flex flex-col min-h-0">
        <BacklogTable subsheetId={isAllItems ? null : selectedSubsheetId} />
      </div>
    </div>
  );
};

export default Index;
